import React, { useCallback, useMemo } from "react";
import {
  AHSeparator,
  Box,
  ListItem,
  SectionHeader,
  useBoxStyle,
  useLayoutContext,
  useTheme,
} from "arkh";
import { FlatList, Platform, Switch } from "react-native";
import { GoalState } from "./types";
import { graphql, useFragment } from "react-relay";
import { KPIEditSectionsComponent_Routine$key } from "./__generated__/KPIEditSectionsComponent_Routine.graphql";
import { KPIEditSectionsSectionHeader } from "./KPIEditSectionsSectionHeader";
import { SectionSubItemContainer } from "../../components/SectionSubItemContainer";
import { KPIAddSubSectionButton } from "../../components/KPIAddSubSectionButton";
import {
  ListSectionHeader,
  ListSectionHeaderTitle,
} from "../../../../components/atoms/ListSectionHeader";
import { GoalDirectionSegmentedControl } from "../../components/GoalDirectionSegmentedControl";
import { FormattedMessage } from "react-intl";
import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import { useModalInsets } from "../../../../components/mobile/useModalInsets";
import { KPIEditScreenProps } from "../KPIEditParamList";
import { KPIEditSectionsComponentSubItemListItem } from "./KPIEditSectionsComponentSubItemListItem";
import { KPIEditSectionsTeamListItem } from "./KPIEditSectionsTeamListItem";
import { KPICreateSubSectionButton } from "../../components/KPICreateSubSectionButton";
import { useKPIEditSectionsSave } from "./useKPIEditSectionsSave";

import { KPILocalSection } from "./KPILocalSection";
import cuid from "cuid";
import { KPIEditSectionsComponent_Store$key } from "./__generated__/KPIEditSectionsComponent_Store.graphql";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
import { useNavigationHeader } from "@/src/common/navigationHeaderOptions";

export function KPIEditSectionsComponent({
  route: { params },
  navigation,
  ...props
}: KPIEditScreenProps<"KPIEditSections"> & {
  store: KPIEditSectionsComponent_Store$key;
  routine: KPIEditSectionsComponent_Routine$key;
}) {
  const intl_tokens = useIntlTokens();
  const theme = useTheme();
  const store = useFragment(
    graphql`
      fragment KPIEditSectionsComponent_Store on Store {
        publicTeams(includeArchived: true) {
          id
          name
          ...KPIEditSectionsComponentSubItemListItem_Team
          ...KPIEditSectionsTeamListItem_Team
          ...KPIEditSectionsSectionHeader_Team
        }
      }
    `,
    props.store
  );
  const routine = useFragment(
    graphql`
      fragment KPIEditSectionsComponent_Routine on Routine {
        id
        activeSections {
          id
          title
          assignedTeam {
            id
          }
        }
        activeVersion {
          tasks {
            id
            __typename
            ... on TaskNumber {
              goals {
                greaterThan
                lessThan
                section {
                  id
                }
              }
            }
          }
        }
      }
    `,
    props.routine
  );
  const task = useMemo(
    () => routine.activeVersion.tasks[0]!,
    [routine.activeVersion.tasks]
  );
  const remoteGoalState: GoalState = useMemo(() => {
    if (task && task.__typename === "TaskNumber") {
      const g = task.goals?.[0] ?? null;
      return g?.lessThan !== null
        ? "less"
        : g?.greaterThan !== null
          ? "greater"
          : null;
    }
    return "greater";
  }, [task]);
  const insets = useModalInsets();
  const contentContainerStyle = useBoxStyle({
    space: "medium",
    insets,
  });

  const remoteGoals = useMemo(() => task.goals ?? [], [task.goals]);
  const hasGoal = useMemo(
    () =>
      params.localGoalsActive !== undefined
        ? params.localGoalsActive
        : remoteGoalState !== null,
    [params.localGoalsActive, remoteGoalState]
  );

  const goalDirection = useMemo(
    () =>
      params.localGoalDirection !== undefined
        ? params.localGoalDirection
        : (remoteGoalState ?? "greater"),
    [params.localGoalDirection, remoteGoalState]
  );

  const remoteSections = useMemo(
    () =>
      routine.activeSections.flatMap((s) => {
        if (
          params.localSections.findIndex(({ id }) => id === s.id) !== -1 ||
          params.removeSections.includes(s.id)
        ) {
          return [];
        }
        const goal = remoteGoals.find((rg) => rg.section.id === s.id);
        return [
          {
            ...s,
            assignedTeam: s.assignedTeam!,
            goalValue: goal?.greaterThan ?? goal?.lessThan ?? 0,
          },
        ];
      }),
    [
      routine.activeSections,
      params.localSections,
      params.removeSections,
      remoteGoals,
    ]
  );

  const sections = useMemo(
    () => [...remoteSections, ...params.localSections],
    [params.localSections, remoteSections]
  );

  const data = useMemo(
    () =>
      store.publicTeams.flatMap((team) => {
        const data = sections
          .filter((s) => s.assignedTeam?.id === team.id)
          .slice()
          .sort((a, b) => (a.title ?? "").localeCompare(b.title ?? ""));
        return data.length > 0
          ? [
              {
                team,
                data,
              },
            ]
          : [];
      }),
    [sections, store.publicTeams]
  );

  const onAddSubTeam = useCallback(
    (teamId?: string) => {
      const common = {
        type: "Edit",
        routineId: routine.id,
        localGoalsActive: params.localGoalsActive,
        localGoalDirection: params.localGoalDirection,
        localSections: params.localSections,
        removeSections: params.removeSections,
        remoteSections,
        title: params.title,
      };
      if (teamId) {
        navigation.navigateDeprecated("KPICreateSubTeam", {
          screen: "KPICreateSubTeamSetName",
          params: {
            ...common,
            name: "",
            teamId,
          },
        });
      } else {
        navigation.navigateDeprecated("KPICreateSubTeam", {
          type: "Edit",
          screen: "KPICreateSubTeamSelectTeam",
          params: common,
        });
      }
    },
    [
      routine.id,
      params.localGoalsActive,
      params.localGoalDirection,
      params.localSections,
      params.removeSections,
      params.title,
      remoteSections,
      navigation,
    ]
  );
  const onEditSubTeam = useCallback(
    (section: KPILocalSection) => {
      navigation.navigateDeprecated("KPIEditSubTeam", {
        section,
        title: params.title,
        remoteSections,
        localSections: params.localSections,
        removeSections: params.removeSections,
        localGoalDirection: params.localGoalDirection,
        localGoalsActive: params.localGoalsActive,
        routineId: params.routineId,
      });
    },
    [navigation, params, remoteSections]
  );

  const onRemoveSection = useCallback(
    (id: string) => {
      navigation.setParams({
        localSections: params.localSections.filter((ls) => ls.id !== id),
        removeSections: [...params.removeSections, id],
      });
    },
    [navigation, params.localSections, params.removeSections]
  );

  const onSetGoalValue = useCallback(
    (section: {
      id: string;
      title: string | null;
      assignedTeam: { id: string };
    }) =>
      (goalValue: number) => {
        const s = params.localSections.findIndex(({ id }) => id === section.id);
        navigation.setParams({
          localSections:
            s === -1
              ? [
                  ...params.localSections,
                  {
                    ...section,
                    goalValue,
                  },
                ]
              : params.localSections.map((ls) =>
                  ls.id === section.id ? { ...ls, goalValue } : ls
                ),
        });
      },
    [params.localSections, navigation]
  );

  const otherTeams = useMemo(
    () =>
      store.publicTeams.filter(
        ({ id }) => !data.map(({ team }) => team.id).includes(id)
      ),
    [store.publicTeams, data]
  );

  const [onSave, loading] = useKPIEditSectionsSave({
    goalState: hasGoal ? goalDirection : null,
    taskId: task.id,
    sections,
    localSections: params.localSections,
    localRemoveSections: params.removeSections,
    remoteSectionIds: routine.activeSections.map(({ id }) => id),
    onCompleted: navigation.goBack,
    routineId: routine.id,
  });

  useNavigationHeader({
    type: "SAVE",
    onSave: onSave,
    saveLoading: loading,

    onCancel: navigation.goBack,
  });

  const onAddTeam = useCallback(
    (teamId: string) => () => {
      navigation.setParams({
        localSections: [
          ...params.localSections,
          {
            title: null,
            id: cuid(),
            goalValue: 0,
            assignedTeam: {
              id: teamId,
            },
          },
        ],
      });
    },
    [navigation, params.localSections]
  );
  const ListHeaderComponent = useMemo(() => {
    return (
      <Box spaceBottom="medium">
        <ListItem
          isFirst
          isLast={!hasGoal}
          right={
            <Switch
              onChange={(event) => {
                navigation.setParams({
                  localGoalsActive: event.nativeEvent.value,
                });
              }}
              onValueChange={(event) => {
                navigation.setParams({
                  localGoalsActive: event,
                });
              }}
              value={hasGoal}
              trackColor={{
                false: theme["surface-backgroundControl"],
                true: theme.primary,
              }}
              {...(Platform.OS === "web"
                ? { activeThumbColor: theme["surface-primary"] }
                : {})}
              thumbColor={theme["surface-primary"]}
            />
          }
        >
          <SectionHeader.Title>
            <FormattedMessage
              defaultMessage="Use targets"
              description="KPIEditSectionsComponent use targets section header"
            />
          </SectionHeader.Title>
        </ListItem>
        {hasGoal ? (
          <Box
            spaceHorizontal="medium"
            spaceBottom="medium"
            color="surface-primary"
            cornerRadiusBottomLeft="large"
            cornerRadiusBottomRight="large"
          >
            <GoalDirectionSegmentedControl
              goalDirection={goalDirection}
              setGoalDirection={(localGoalDirection) => {
                navigation.setParams({ localGoalDirection });
              }}
            />
          </Box>
        ) : null}
      </Box>
    );
  }, [goalDirection, hasGoal, navigation, theme]);

  const ListFooterComponent = useMemo(
    () => (
      <Box spaceVertical="medium">
        {otherTeams.length > 0 ? (
          <ListSectionHeader>
            <ListSectionHeaderTitle>
              {intl_tokens.AddMesurementAreas}
            </ListSectionHeaderTitle>
          </ListSectionHeader>
        ) : null}
        {otherTeams.map((team, index) => (
          <React.Fragment key={`team-list-item-${team.id}`}>
            {index === 0 ? null : <AHSeparator />}
            <KPIEditSectionsTeamListItem
              team={team}
              isFirst={index === 0}
              isLast={index === otherTeams.length - 1}
              onPress={onAddTeam(team.id)}
            />
          </React.Fragment>
        ))}
        <KPICreateSubSectionButton onPress={() => onAddSubTeam()} />
      </Box>
    ),
    [otherTeams, intl_tokens.AddMesurementAreas, onAddTeam, onAddSubTeam]
  );

  const layout = useLayoutContext();

  return (
    <>
      <FlatList
        contentContainerStyle={contentContainerStyle}
        data={data}
        renderScrollComponent={(p) => <KeyboardAwareScrollView {...p} />}
        keyExtractor={(item) => `team-${item.team.id}`}
        ItemSeparatorComponent={AHSeparator}
        keyboardShouldPersistTaps="handled"
        renderItem={({ item, index }) => {
          const single = item.data.length === 1 && item.data[0].title === null;
          return (
            <React.Fragment key={`team-${item.team.id}`}>
              <KPIEditSectionsSectionHeader
                hasSubTeam={!single}
                goalDirection={goalDirection}
                goalValue={item.data[0].goalValue}
                hasGoal={single && hasGoal}
                isFirst={index === 0}
                isLast={index === data.length - 1 && single}
                onAddSubTeam={
                  single ? () => onAddSubTeam(item.team.id) : undefined
                }
                onRemoveSection={
                  single
                    ? () => {
                        onRemoveSection(item.data[0].id);
                      }
                    : undefined
                }
                setGoalValue={onSetGoalValue(item.data[0])}
                team={item.team}
              />
              {single ? null : (
                <>
                  {item.data.map((sub, idx) => (
                    <KPIEditSectionsComponentSubItemListItem
                      key={sub.id}
                      isLast={idx === item.data.length - 1}
                      goalDirection={goalDirection}
                      goalValue={sub.goalValue}
                      team={item.team}
                      title={sub.title ?? item.team.name ?? ""}
                      setGoalValue={hasGoal ? onSetGoalValue(sub) : undefined}
                      onEditSubsection={() => onEditSubTeam(sub)}
                    />
                  ))}
                  <SectionSubItemContainer isLast={index === data.length - 1}>
                    <KPIAddSubSectionButton
                      style={{
                        paddingLeft: layout.space.medium + layout.space.small,
                      }}
                      spaceRight="medium"
                      spaceVertical="medium"
                      onPress={() => onAddSubTeam(item.team.id)}
                    />
                  </SectionSubItemContainer>
                </>
              )}
            </React.Fragment>
          );
        }}
        ListHeaderComponent={ListHeaderComponent}
        ListFooterComponent={ListFooterComponent}
      />
    </>
  );
}
